import { criteriaFromParams, criteriaToParams, hasAnyCriteria, type StatewideCriteria } from "./statewide-search";

/**
 * A statewide search written by someone signed out, held while they sign in.
 *
 * It used to ride in `/auth?redirect=/map?owner=SMITH+JOHN&addr=…`, and GA4
 * records the full `/auth` URL on its page view: `redirect` is not on the
 * redaction list (and should not need to be), so every gated search sent a
 * typed owner name and address to Google. The criteria wait here instead, and
 * the redirect carries only `RESUME_PARAM`, which says to look.
 *
 * localStorage, not sessionStorage: the email-confirmation link often opens in
 * a new tab, which has no copy of the first tab's session storage. Kept for
 * `PENDING_TTL_MS` and consumed on first read, so a search abandoned at the
 * sign-in form is not run days later.
 *
 * The daily cap's Lite offer saves one too, marked `after: "checkout"`: Stripe
 * returns to `/`, and once Lite shows on the account `CheckoutHandler` sends
 * the buyer to `/map?resume=1`, where the search they were refused runs.
 */
export const PENDING_STATEWIDE_SEARCH_KEY = "wpm_pending_statewide_search";

/** On `/map`, "seed the form from the pending search". Never carries the search itself. */
export const RESUME_PARAM = "resume";

export const PENDING_TTL_MS = 60 * 60 * 1000;

export type PendingAfter = "sign_in" | "checkout";

export function savePendingStatewideSearch(
  criteria: StatewideCriteria,
  now = Date.now(),
  after: PendingAfter = "sign_in",
): void {
  try {
    localStorage.setItem(
      PENDING_STATEWIDE_SEARCH_KEY,
      JSON.stringify({ params: criteriaToParams(criteria), savedAt: now, after }),
    );
  } catch {
    // Storage blocked (private window, site data refused): signing in still
    // works, the form just comes back empty, as it did before any of this.
  }
}

/** The pending search, once: it is removed as it is read. */
export function takePendingStatewideSearch(now = Date.now()): StatewideCriteria | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_STATEWIDE_SEARCH_KEY);
    localStorage.removeItem(PENDING_STATEWIDE_SEARCH_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as { params?: Record<string, unknown>; savedAt?: unknown };
    if (typeof stored.savedAt !== "number" || now - stored.savedAt > PENDING_TTL_MS) return null;
    const params = stored.params ?? {};
    const criteria = criteriaFromParams((name) => {
      const value = params[name];
      return typeof value === "string" ? value : null;
    });
    return hasAnyCriteria(criteria) ? criteria : null;
  } catch {
    return null;
  }
}

/** Whether a search refused by the cap is waiting for its checkout. Leaves it in place. */
export function pendingSearchAwaitsCheckout(now = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(PENDING_STATEWIDE_SEARCH_KEY);
    if (!raw) return false;
    const stored = JSON.parse(raw) as { savedAt?: unknown; after?: unknown };
    return stored.after === "checkout" && typeof stored.savedAt === "number" && now - stored.savedAt <= PENDING_TTL_MS;
  } catch {
    return false;
  }
}
